import type { FeePreview, PricingRule, TablePricingRule } from '../types';
import { formatCurrency, formatCurrencyMajor } from '../utils/theme';

/**
 * One unit: `total_with_fees` (cents) → discountable list base vs fixed fees+tax (not reduced by % promos).
 */
export function splitFeePreviewUnitToDiscountableAndFixedCents(
  totalWithFees: number,
  feesTotal: number | undefined,
  taxTotal: number | undefined,
  listBaseCentsFallback: number,
): { discountable: number; fixed: number } {
  const tw = Math.round(totalWithFees);
  const f = feesTotal != null && Number.isFinite(feesTotal) ? Math.round(feesTotal) : 0;
  const t = taxTotal != null && Number.isFinite(taxTotal) ? Math.round(taxTotal) : 0;
  if (f > 0 || t > 0) {
    const base = tw - f - t;
    if (base >= 0) return { discountable: base, fixed: f + t };
  }
  const list = Math.round(listBaseCentsFallback);
  if (list > 0 && list <= tw) {
    return { discountable: list, fixed: tw - list };
  }
  return { discountable: tw, fixed: 0 };
}

export function applyPromoRespectingFixedFeesCents(
  discountableCents: number,
  fixedCents: number,
  discount: { type: 'percentage' | 'fixed'; value: number } | null,
): number {
  if (!discount) return Math.round(discountableCents) + Math.round(fixedCents);
  if (discount.type === 'percentage') {
    return (
      Math.max(0, Math.round(discountableCents * (1 - discount.value / 100))) + Math.round(fixedCents)
    );
  }
  return (
    Math.max(0, Math.round(discountableCents) - Math.round(discount.value)) + Math.round(fixedCents)
  );
}

/** Join API `fee_preview.inclusions` for subtext — do not compute fees locally. */
export function formatFeePreviewInclusions(feePreview: FeePreview | null | undefined): string {
  if (!feePreview?.inclusions?.length) return '';
  return feePreview.inclusions.filter((s) => typeof s === 'string' && s.trim()).join(' / ');
}

/**
 * Widget seat-map / doc: use `formatted_total` (all-in) for what the customer pays when `fee_preview` exists.
 * Fallback: list `price` in major.
 */
export function getTableRulePriceDisplay(
  rule: Pick<TablePricingRule, 'price' | 'fee_preview' | 'formatted_for_display'> | undefined,
  currency: string,
): string {
  if (!rule) return '';
  /** All-in from API — prefer over list-only `formatted_for_display` */
  if (rule.fee_preview?.formatted_total?.trim()) return rule.fee_preview.formatted_total;
  if (rule.formatted_for_display?.trim()) return rule.formatted_for_display;
  return formatCurrencyMajor(rule.price, currency);
}

/** Seat `price` is in cents; all-in: `fee_preview.formatted_total` or list display. */
export function getSeatRulePriceDisplay(
  rule: Pick<PricingRule, 'price' | 'fee_preview' | 'formatted_for_display'> | undefined,
  currency: string,
): string {
  if (!rule) return '';
  if (rule.fee_preview?.formatted_total?.trim()) return rule.fee_preview.formatted_total;
  if (rule.formatted_for_display?.trim()) return rule.formatted_for_display;
  return formatCurrency(rule.price, currency);
}

/** Selection summary total: prefer `total_with_fees` (cents) as major, else list path. */
export function lineTotalMajorForTableRule(rule: TablePricingRule | undefined): number {
  if (!rule) return 0;
  const tw = rule.fee_preview?.total_with_fees;
  if (tw != null && Number.isFinite(tw)) return tw / 100;
  return rule.price || 0;
}

export function lineTotalMajorForSeatRule(rule: PricingRule | undefined): number {
  if (!rule) return 0;
  const tw = rule.fee_preview?.total_with_fees;
  if (tw != null && Number.isFinite(tw)) return tw / 100;
  return (rule.price || 0) / 100;
}

/** Seat-map / `table_pricing` API row (before normalizing to `TablePricingRule`). */
export function getFeePreviewInclusionsFromUnknownRow(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  const fp = (row as { fee_preview?: FeePreview | null }).fee_preview;
  return formatFeePreviewInclusions(fp);
}

/**
 * List `price` in major units + optional raw API row — show all-in (`formatted_total`) when `fee_preview` exists.
 */
export function getTablePriceLabelFromListAndRaw(
  listPriceMajor: number,
  raw: unknown,
  currency: string,
): string {
  if (!raw || typeof raw !== 'object') {
    return formatCurrencyMajor(listPriceMajor, currency);
  }
  const o = raw as { fee_preview?: FeePreview | null; formatted_for_display?: string };
  return getTableRulePriceDisplay(
    {
      price: listPriceMajor,
      fee_preview: o.fee_preview ?? undefined,
      formatted_for_display: o.formatted_for_display,
    },
    currency,
  );
}
